<template v-slot:top>
  <div>
    <!-- Toolbar -->
    <div class="scanner" style="height: 100vh;">
      <h2 class="scanTitle">Scan Your Book Here</h2>
      <v-container fill-height class="d-flex align-center justify-center">
        <v-row align="center" justify="center" class="text-center">
          <v-col cols="12" md="8" class="d-flex justify-center">
            <input
              ref="scannerInput"
              aria-hidden="true"
              autofocus
              tabindex="0"
              @blur="refocusInput"
              style="position: absolute; opacity: 0; width: 0; height: 0; pointer-events: none;"
            />
          </v-col>
        </v-row>
      </v-container>
    </div>

    <!-- Scanner Content -->
    <v-container class="scannerContainer">
      <v-row align="center" justify="center">
        <!-- Fail Modal -->
        <v-dialog v-model="notWinnerModel" max-width="480" overlay-color="black" overlay-opacity="1">
          <v-card class="customModal customModalFail">
            <div class="iconContainer">
              <img class="cloverIcon" src="../../assets/clover.png" alt="icon" />
            </div>
            <v-card-text class="text-center modalContent modalContentFail mt-6">
              <h1 class="modalTitleFail" :class="{ 'errorMessageFont': errorMessage }">
                {{ errorMessage || (alreadyPrinted ? "Already Scanned" : "Better Luck Next Time") }}
              </h1>
              <p class="modalSubtitleFail" v-if="!errorMessage">
                {{
                  alreadyPrinted
                    ? "Looks like this sheet was already scanned. Thanks for playing!"
                    : "We Hope To See You Play Again Soon!"
                }}
              </p>
            </v-card-text>
            <v-card-actions class="justify-center">
              <button type="button" class="customBtn" @click="notWinnerModel = false">
                <img src="../../assets/scan-icon.svg" alt="scanner" />
                Scan Book
              </button>
            </v-card-actions>
          </v-card>
        </v-dialog>

        <!-- Success Modal -->
        <v-dialog v-model="winnerModel" max-width="100vw" class="customDialog winnerModalMain" overlay-opacity="1"
          overlay-color="black">
          <v-row class="winnerModalInner">
            <div class="winnerDialog">
              <v-card class="customModal congratsBox">
                <div class="iconContainer winnerIcon">
                  <img class="congratsIcon" src="../../assets/congratulations.png" alt="congrats" />
                </div>
                <v-card-text class="text-center modalContent">
                  <h2 class="modalSubtitle">You Are A Winner!</h2>
                  <h1 class="modalTitle">Congratulations!</h1>
                  <v-divider class="my-4 winnerDivider"></v-divider>
                </v-card-text>
              </v-card>

              <v-card class="winnerModal">
                <v-card-text>
                  <v-row class="detailsSection">
                    <v-col cols="6" class="text-left detailLabel py-0">Status:</v-col>
                    <v-col cols="6" class="text-right detailValue py-0">WINNER</v-col>

                    <v-col cols="6" class="text-left detailLabel gameRow">Game:</v-col>
                    <v-col cols="6" class="text-right detailValue gameRow">{{
                      gameTitle
                    }}</v-col>
                    <v-col v-for="(winner, index) in winnerEntries" :key="index" cols="12">
                      <v-row>
                        <v-col cols="6" class="text-left detailLabel pb-0">Winning Pattern:</v-col>
                        <v-col cols="6" class="text-right detailValue pb-0">
                          {{
                            getCurrentWinningPatternText(winner.winningPattern, winner.winnerCountOnOrdinal > 1 ? true :
                              false)
                          }}
                        </v-col>
                        <v-col cols="6" class="text-left detailLabel gameRow">Panel Number:</v-col>
                        <v-col cols="6" class="text-right detailValue gameRow">{{ winner.panelId }}</v-col>
                      </v-row>
                    </v-col>

                    <v-col cols="6" class="text-left detailLabel gameRow">Winning Amount:</v-col>
                    <v-col cols="6" class="text-right detailValue gameRow">€{{ totalWinningAmount }}</v-col>
                  </v-row>
                </v-card-text>
              </v-card>

              <v-card-actions class="justify-center">
                <button @click="winnerModel = false" type="button" class="customBtn">
                  <img src="../../assets/scan-icon.svg" alt="scanner" />
                  Scan Book
                </button>
              </v-card-actions>
            </div>
          </v-row>
        </v-dialog>
      </v-row>
    </v-container>
  </div>
</template>

<script>
import { mapActions } from "vuex";

const MIN_BARCODE_LENGTH = 22; // minimum expected length for uuid
const SCAN_TIMEOUT_MS = 100; // Zebra scanner is quick, so if gap between keystrokes exceeds this it resets the buffer

export default {
  name: "Scanner",
  data: () => ({
    notWinnerModel: false,
    alreadyPrinted: false,
    winnerModel: false,
    winnerData: null,
    errorMessage: null,
    isScanningEnabled: true,
    barcodeBuffer: "",
    scanTimeoutId: null,
  }),
  computed: {
    gameTitle() {
      return this.winnerData?.gameTitle || "N/A";
    },
    winnerEntries() {
      return this.winnerData?.winnerEnt || [];
    },
    detailedWinnerInfo() {
      if (!this.winnerEntries.length || !this.winnerData?.winningPatternsList)
        return [];

      return this.winnerEntries.map((winner) => {
        const pattern = this.winnerData.winningPatternsList.find(
          (item) => item.winningPattern === winner.winningPattern
        );
        const prize = pattern?.intTwo || 0;
        const winnerCount = parseInt(winner.winnerCountOnOrdinal || 1, 10);

        const dividedPrize = winnerCount > 1 ? prize / winnerCount : prize;
        const sharedTag = winnerCount > 1 ? " (shared)" : "";

        return {
          ...winner,
          dividedPrize,
          displayPatternText:
            this.getCurrentWinningPatternText(winner.winningPattern) +
            sharedTag,
        };
      });
    },
    totalWinningAmount() {
      return this.detailedWinnerInfo.reduce(
        (sum, winner) => sum + winner.dividedPrize,
        0
      );
    },
  },
  methods: {
    ...mapActions([
      "checkBookWinner",
      "printWinnnerRecipt",
      "updateWinnnerRecipt",
    ]),
    handleKeyInput(event) {
      if (this.winnerModel || this.notWinnerModel) return;

      if (event.key === "Enter") {
        clearTimeout(this.scanTimeoutId);
        this.scanTimeoutId = null;
        const barcode = this.barcodeBuffer.trim();
        this.barcodeBuffer = "";
        if (barcode.length >= MIN_BARCODE_LENGTH) {
          this.onDecode(barcode);
        }
        return;
      }

      if (event.key.length !== 1) return;

      clearTimeout(this.scanTimeoutId);
      this.scanTimeoutId = setTimeout(() => {
        const barcode = this.barcodeBuffer.trim();
        this.barcodeBuffer = "";
        this.scanTimeoutId = null;
        if (barcode.length >= MIN_BARCODE_LENGTH) {
          this.onDecode(barcode);
        }
      }, SCAN_TIMEOUT_MS);

      this.barcodeBuffer += event.key;
    },
    refocusInput() {
      if (this.winnerModel || this.notWinnerModel) return;
      this.$refs.scannerInput && this.$refs.scannerInput.focus();
    },
    resetModalsAfterDelay() {
      setTimeout(() => {
        this.notWinnerModel = false;
        this.winnerModel = false;
        this.winnerData = null;
        this.alreadyPrinted = false;
        this.errorMessage = "";
        this.isScanningEnabled = true;
        this.refocusInput();
      }, 10000);
    },
    getCurrentWinningAmount(winpattern) {
      const pattern = this.winnerData?.winningPatternsList?.find(
        (item) => item.winningPattern === winpattern
      );
      return pattern?.intTwo !== undefined && pattern?.intTwo !== null
        ? `€${pattern.intTwo}`
        : "€0";
    },
    getCurrentWinningPatternText(currentWinningPattern, isShared = false) {
      let patternText = "";
      switch (currentWinningPattern) {
        case "ONE_LINE":
          patternText = "One Line";
          break;
        case "TWO_LINE":
          patternText = "Two Line";
          break;
        case "FULL_HOUSE":
          patternText = "Full House";
          break;
        case "JACKPOT":
          patternText = "Jackpot";
          break;
        default:
          patternText = "Winner";
      }
      return isShared ? `${patternText} (shared)` : patternText;
    },
    async onDecode(result) {
      if (!this.isScanningEnabled) return;

      this.isScanningEnabled = false;

      try {
        if (!this.winnerModel) {
          const winnerResponse = await this.checkBookWinner({ bookId: result });
          if (winnerResponse.data.requestMode === "FAILURE") {
            this.errorMessage = winnerResponse.data.message;
            this.notWinnerModel = true;
            this.winnerModel = false;
            this.winnerData = null;
          } else {
            this.errorMessage = null;
          }
          if (
            Array.isArray(winnerResponse.data?.winnerEnt) &&
            winnerResponse.data.winnerEnt.length > 0
          ) {
            const alreadyPrinted = winnerResponse.data.winnerEnt.some(
              (w) => w.receiptPrinted === true
            );
            if (!alreadyPrinted) {
              this.winnerData = winnerResponse.data;
              this.winnerModel = true;
              this.notWinnerModel = false;
              await this.updateWinnnerRecipt({ bookId: result });
              await this.printWinnnerRecipt(winnerResponse.data);
            } else {
              this.alreadyPrinted = true;
              this.notWinnerModel = true;
              this.winnerModel = false;
            }
          } else {
            this.alreadyPrinted = false;
            this.winnerData = null;
            this.winnerModel = false;
            this.notWinnerModel = true;
          }
        }
      } catch (error) {
        if (error.response) {
          this.errorMessage = error.response.data.message;
          this.winnerData = null;
          this.winnerModel = false;
          this.notWinnerModel = true;
          this.alreadyPrinted = false;
        }
      } finally {
        this.resetModalsAfterDelay();
      }
    },
    handleRegistrationComplete(response) {
      if (response.success) {
        this.winnerData = response.data;
        this.winnerModel = true;
      } else {
        this.notWinnerModel = true;
      }
    },
  },
  mounted() {
    window.addEventListener("keydown", this.handleKeyInput);
    this.$nextTick(() => {
      this.refocusInput();
    });
  },
  beforeDestroy() {
    window.removeEventListener("keydown", this.handleKeyInput);
    clearTimeout(this.scanTimeoutId);
  },
  unmounted() {
    window.removeEventListener("keydown", this.handleKeyInput);
    clearTimeout(this.scanTimeoutId);
  },
};
</script>

<style scoped>
.scanTitle {
  font-size: 28px;
  padding: 10px 0px 0px 0px;
  text-align: center;
}

.winnerModalInner {
  display: flex;
  justify-content: center;
  align-items: center;
  background-image: url(../../assets/lottoballs-bg.png);
}

.winnerDivider {
  display: none;
}

.winnerDialog {
  max-width: 481px;
}

.scannerContainer {
  display: flex;
  justify-content: center;
  align-items: center;
}

.customModal {
  border-radius: 39px;
  background: linear-gradient(to bottom, #484a4a, #2b2a27, #484a4a);
  backdrop-filter: blur(50.266666412353516px);
  fill: rgba(255, 255, 255, 0.1);
  position: relative;
  padding-top: 30px;
  color: white;
  margin-top: 35px;
}

.congratsBox {
  background: linear-gradient(to bottom, #484a4a, #2b2a27, #484a4a);
}

.winnerModalMain {
  display: flex;
  gap: 20px;
}

.winnerModal {
  background-color: black;
  background-image: url(../../assets/winner-bg.png);
  background-size: cover;
  background-repeat: no-repeat;
  position: relative;
  padding: 50px 10px;
  color: white;
  margin: 14px 0px;
}

.iconContainer {
  position: absolute;
  top: -30px;
  left: 50%;
  transform: translateX(-50%);
  border-radius: 50%;
  width: 60px;
  height: 60px;
  display: flex;
  justify-content: center;
  align-items: center;
}


.modalContent {
  padding: 20px;
  background-color: 464848;
}

.modalTitle {
  color: #fb2;
  text-align: center;
  font-family: "Arial Rounded MT Bold";
  font-size: 48px;
  font-style: normal;
  font-weight: 400;

  /* 139.583% */
  text-transform: capitalize;
}

.modalTitleFail {
  color: #fff;
  text-align: center;
  font-family: "Arial Rounded MT Bold";
  font-size: 56px;
  font-style: normal;
  font-weight: 400;
  line-height: normal;
  text-transform: capitalize;
}

.errorMessageFont {
  font-size: 32px !important;
}

.modalSubtitle {
  font-size: 1.5rem;
  color: white;
  padding: 20px 0px 10px 0px;
}

.customModal:first-of-type .modalSubtitle {
  color: #fff;
  text-align: center;
  font-family: "Arial Rounded MT Bold";
  font-size: 40px;
  font-style: normal;
  font-weight: 400;
  line-height: 50px;
  /* 167.5% */
  text-transform: capitalize;
}

.customModal:first-of-type .modalSubtitleFail {
  color: #edb73d;
  text-align: center;
  font-family: "Arial Rounded MT Bold";
  font-size: 20px;
  font-style: normal;
  font-weight: 400;
  line-height: normal;
  text-transform: capitalize;
  padding-top: 30px;
}

.detailsSection {
  font-size: 1rem;
  color: white;
}

.detailLabel {
  color: #fff;
  font-family: "Arial Rounded MT Bold";
  font-size: 20px;
  font-style: normal;
  font-weight: 400;
  line-height: normal;
  letter-spacing: 1.68px;
  text-transform: capitalize;
}

.detailValue {
  color: #fb2;
  font-family: "Arial Rounded MT Bold";
  font-size: 20px;
  font-style: normal;
  font-weight: 400;
  line-height: normal;
  letter-spacing: 1.68px;
  text-transform: capitalize;
}

.detailValue:first-of-type {
  color: #ffca28;
}

.statusRow {
  border-bottom: 1px solid #555;
  padding-bottom: 8px;
  margin-bottom: 8px;
}

.gameRow {
  border-bottom: 1px solid #555;

}

.customBtn {
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 4px;
  color: #fff;
  text-align: center;
  font-family: "Arial Rounded MT Bold";
  font-size: 24px;
  font-weight: 400;
  width: 100%;
  height: 46px;
  flex-shrink: 0;
  border-radius: 32px;
  background: var(--22, linear-gradient(180deg, #fb2 0%, #ac7b0e 100%));
  box-shadow: 50.267px -50.267px 50.267px 0px rgba(194, 194, 194, 0.1) inset,
    -50.267px 50.267px 50.267px 0px rgba(255, 255, 255, 0.1) inset;
  backdrop-filter: blur(50.266666412353516px);
}

.customBtn .v-icon {
  margin-right: 8px;
}

.cloverIcon {
  border-radius: 50%;
  border: 4px solid black;
}

.btnContainer {
  display: flex;
  gap: 20px;
}

.btnContainer button {
  background-color: #ac7b0e;
  color: white;
  padding: 10px;
  border-radius: 10px;
  border: none;
}

.congratsIcon {
  border-radius: 50%;
  border: 4px solid black;
}

@media screen and (max-width: 1500px) {
  .winnerDivider {
    display: block;
  }

  .customModal {
    margin-top: 20px;
    padding-top: 20px;
  }

  .winnerModal {
    background-size: cover;
    padding: 15px 10px;
    margin: 0px;
  }

  .modalContent {
    padding: 0px !important;
  }

  .modalTitle {
    font-size: 32px;
  }

  .customModal:first-of-type .modalSubtitle {
    font-size: 26px;
    line-height: 26px;
  }

  .winnerIcon img {
    height: 70px;
    width: 70px;

  }

  .modalTitleFail {
    font-size: 40px;
    padding: 40px 80px 10px 80px;
  }

  .customModal:first-of-type .modalSubtitleFail {
    padding: 0px 20px 20px 20px;
    font-size: 18px;
    line-height: 30px;
  }

}
</style>
